import {
    EXECUTION_STATUS_TERMINAL,
    SERVER_NODE_TYPE_MAP,
    FUNCTION_KEY_BY_CODE_MAP,
    GROUP_MIN_WIDTH,
    GROUP_MIN_HEIGHT,
    IMAGE_NODE_WIDTH,
    IMAGE_NODE_HEIGHT,
    DEFAULT_VIEWPORT,
} from '../constants/canvas';
import {
    getVersionNumber,
    toPositiveNumber,
    getParamKeySlotMap,
} from './common';
import {
    withNodeImageSequence,
    getNodeImageSequence,
    parseLabelSequence,
} from './imageSequence';

export const mergeProjectRecord = (currentProject, incomingProject) => {
    if (!currentProject) return incomingProject;
    if (!incomingProject) return currentProject;

    return {
        ...currentProject,
        ...incomingProject,
        version: Math.max(
            getVersionNumber(currentProject.version),
            getVersionNumber(incomingProject.version),
        ),
    };
};

export const mergeProjectCollections = (currentProjects, incomingProjects, append = false) => {
    const mergedMap = new Map(
        (append ? currentProjects : []).map((project) => [project.id, project]),
    );

    incomingProjects.forEach((project) => {
        const existingProject = mergedMap.get(project.id);
        mergedMap.set(project.id, mergeProjectRecord(existingProject, project));
    });

    if (!append) {
        return incomingProjects.map((project) => mergedMap.get(project.id) || project);
    }

    const orderedProjects = [...currentProjects];
    incomingProjects.forEach((project) => {
        if (!orderedProjects.some((item) => item.id === project.id)) {
            orderedProjects.push(project);
        }
    });

    return orderedProjects.map((project) => mergedMap.get(project.id) || project);
};

export const reorderProjectCollections = (projects, oldIdx, newIdx) => {
    if (!Array.isArray(projects) || oldIdx === newIdx) return projects;
    if (oldIdx < 0 || newIdx < 0 || oldIdx >= projects.length || newIdx >= projects.length) return projects;

    const nextProjects = [...projects];
    const [movedProject] = nextProjects.splice(oldIdx, 1);
    nextProjects.splice(newIdx, 0, movedProject);
    return nextProjects;
};

export const getProjectViewport = (projectDetail) => {
    const viewport = projectDetail?.nodes?.find((node) => (
        node?.renderData?.canvasViewport
    ))?.renderData?.canvasViewport;

    if (!viewport) return null;

    return {
        x: Number(viewport.x ?? DEFAULT_VIEWPORT.x),
        y: Number(viewport.y ?? DEFAULT_VIEWPORT.y),
        zoom: Number(viewport.zoom ?? DEFAULT_VIEWPORT.zoom),
    };
};

// 将服务端节点结构还原成前端画布节点
export const restoreNodeFromProject = (node) => {
    const renderData = node?.renderData || {};
    const renderNodeData = renderData?.data || {};
    const serverLatestExecution = node?.latestExecution && typeof node.latestExecution === 'object'
        ? node.latestExecution
        : null;
    const renderLatestExecution = renderNodeData?.latestExecution && typeof renderNodeData.latestExecution === 'object'
        ? renderNodeData.latestExecution
        : null;
    const serverLatestStatus = String(serverLatestExecution?.status || '').toLowerCase();
    const renderLatestStatus = String(renderLatestExecution?.status || '').toLowerCase();
    const latestExecution = serverLatestStatus && EXECUTION_STATUS_TERMINAL.has(serverLatestStatus)
        ? serverLatestExecution
        : (serverLatestExecution || renderLatestExecution);
    const latestStatus = String(latestExecution?.status || renderLatestStatus || '').toLowerCase();
    const type = renderData?.type || SERVER_NODE_TYPE_MAP[node?.nodeType] || 'imageNode';
    const functionKey = renderNodeData?.functionKey || FUNCTION_KEY_BY_CODE_MAP[node?.functionCode] || '';
    const fallbackNodeSize = type === 'groupNode'
        ? { width: GROUP_MIN_WIDTH, height: GROUP_MIN_HEIGHT }
        : { width: IMAGE_NODE_WIDTH, height: IMAGE_NODE_HEIGHT };
    const width = toPositiveNumber(renderData?.width)
        ?? toPositiveNumber(renderData?.style?.width)
        ?? fallbackNodeSize.width;
    const height = toPositiveNumber(renderData?.height)
        ?? toPositiveNumber(renderData?.style?.height)
        ?? fallbackNodeSize.height;
    const position = {
        x: Number(renderData?.position?.x ?? 0),
        y: Number(renderData?.position?.y ?? 0),
    };

    return withNodeImageSequence({
        id: String(node?.id ?? ''),
        type,
        position,
        parentId: node?.parentId || renderData?.parentId || undefined,
        extent: renderData?.extent,
        expandParent: renderData?.expandParent,
        style: {
            ...(renderData?.style || {}),
            ...(type === 'groupNode' ? { width, height } : {}),
        },
        width,
        height,
        selected: false,
        data: {
            ...renderNodeData,
            label: renderNodeData?.label || node?.nodeName || node?.functionName || '节点',
            src: renderNodeData?.src || '',
            functionKey,
            selectedOutputId: renderNodeData?.selectedOutputId,
            activeExecutionId: latestExecution?.executionId || renderNodeData?.activeExecutionId || '',
            inputParams: node?.inputParams || renderNodeData?.inputParams || {},
            panelState: renderNodeData?.panelState || {},
            displayMode: renderNodeData?.displayMode || 'empty',
            latestExecution,
            executionStatus: (() => {
                // latestExecution 已是终态时以它为准，修正 renderData 里可能残留的过时 processing 状态
                if (latestStatus && EXECUTION_STATUS_TERMINAL.has(latestStatus)) {
                    return latestStatus;
                }
                return renderNodeData?.executionStatus || latestStatus || 'idle';
            })(),
            groupExecutionLocked: !!renderNodeData?.groupExecutionLocked,
        },
    }, getNodeImageSequence({
        data: renderNodeData,
        renderData,
    }) || (type !== 'groupNode'
        ? (
            parseLabelSequence(renderNodeData?.label || node?.nodeName || '', '图')
            || parseLabelSequence(renderNodeData?.label || node?.nodeName || '', 'Image')
        )
        : 0));
};

export const restoreEdgeFromProject = (edge, nodeMap) => {
    const renderData = edge?.renderData || {};
    const targetNode = nodeMap.get(edge?.targetNodeId);
    const targetFunctionKey = FUNCTION_KEY_BY_CODE_MAP[targetNode?.functionCode] || targetNode?.renderData?.data?.functionKey || '';
    const paramKeySlotMap = getParamKeySlotMap(targetFunctionKey);
    const fallbackSlotKey = targetNode?.paramBindings?.find((binding) => (
        binding?.sourceNodeId === edge?.sourceNodeId
    ))?.paramKey;

    return {
        id: String(edge?.id ?? ''),
        source: String(edge?.sourceNodeId ?? ''),
        target: String(edge?.targetNodeId ?? ''),
        type: renderData?.type || 'flowEdge',
        selected: false,
        data: {
            ...(renderData?.inputSlotKey || fallbackSlotKey ? {
                inputSlotKey: renderData?.inputSlotKey || paramKeySlotMap[fallbackSlotKey] || undefined,
            } : {}),
        },
    };
};

export const restoreEdgesFromProject = (edges = [], nodeMap) => {
    const usedCreativeColorSlots = new Map();

    return edges.map((edge) => {
        const restoredEdge = restoreEdgeFromProject(edge, nodeMap);
        const targetNode = nodeMap.get(edge?.targetNodeId);
        const targetFunctionKey = FUNCTION_KEY_BY_CODE_MAP[targetNode?.functionCode]
            || targetNode?.renderData?.data?.functionKey
            || '';

        if (targetFunctionKey !== 'creativeColor') return restoredEdge;

        const targetId = String(restoredEdge.target || '');
        const usedSlots = usedCreativeColorSlots.get(targetId) || new Set();
        const currentSlotKey = restoredEdge.data?.inputSlotKey;
        const nextSlotKey = currentSlotKey
            || ['source', 'reference'].find((slotKey) => !usedSlots.has(slotKey));

        if (nextSlotKey) usedSlots.add(nextSlotKey);
        usedCreativeColorSlots.set(targetId, usedSlots);

        if (!nextSlotKey || currentSlotKey) return restoredEdge;

        return {
            ...restoredEdge,
            data: {
                ...(restoredEdge.data || {}),
                inputSlotKey: nextSlotKey,
            },
        };
    });
};